import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import theme from '../theme';
import {
  ACHIEVEMENT_STUDIO_PATH,
  ARCHIVE_API_PATH,
  AchievementRendererCard,
  achievementAsset,
  achievementTitle,
  buildPublicRows,
  publicShapeClass,
} from './achievements/shared';

const BOARD_EDITOR_PATH = '/achievements/board';

function publishedDate(record) {
  if (!record?.publishedAt) {
    return '';
  }

  const date = new Date(record.publishedAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function dateValue(record) {
  const value = record?.publishedAt || record?.createdAt || record?.updatedAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function sortByRecent(records) {
  return [...records].sort((left, right) => dateValue(right) - dateValue(left) || Number(right.id || 0) - Number(left.id || 0));
}

function PublishedDate({ record }) {
  const date = publishedDate(record);
  if (!date) {
    return null;
  }

  return (
    <span className="text-xs font-bold uppercase text-emerald-700/70">
      Published {date}
    </span>
  );
}

function PublicAchievementCard({ item, onOpen }) {
  const cardRef = useRef(null);

  const handlePointerMove = (event) => {
    const node = cardRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    const tiltY = (x - 0.5) * 7;
    const tiltX = (0.5 - y) * 5;
    node.style.setProperty('--tilt-x', `${tiltX.toFixed(2)}deg`);
    node.style.setProperty('--tilt-y', `${tiltY.toFixed(2)}deg`);
    node.style.setProperty('--glow-x', `${(x * 100).toFixed(1)}%`);
    node.style.setProperty('--glow-y', `${(y * 100).toFixed(1)}%`);
    node.style.setProperty('--edge-top', (1 + (0.5 - y) * 0.14).toFixed(3));
    node.style.setProperty('--edge-right', (1 + (x - 0.5) * 0.12).toFixed(3));
    node.style.setProperty('--edge-bottom', (1 + (y - 0.5) * 0.14).toFixed(3));
    node.style.setProperty('--edge-left', (1 + (0.5 - x) * 0.12).toFixed(3));
  };

  const handlePointerLeave = () => {
    const node = cardRef.current;
    if (!node) return;
    node.style.setProperty('--tilt-x', '0deg');
    node.style.setProperty('--tilt-y', '0deg');
    node.style.setProperty('--edge-top', '1');
    node.style.setProperty('--edge-right', '1');
    node.style.setProperty('--edge-bottom', '1');
    node.style.setProperty('--edge-left', '1');
    node.style.setProperty('--glow-x', '50%');
    node.style.setProperty('--glow-y', '50%');
  };

  return (
    <button
      ref={cardRef}
      type="button"
      data-achievement-id={item.id}
      onClick={() => onOpen(item)}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      className={`achievement-public-card ${publicShapeClass(item)}`}
      style={{
        '--tilt-x': '0deg',
        '--tilt-y': '0deg',
        '--glow-x': '50%',
        '--glow-y': '50%',
        '--edge-top': 1,
        '--edge-right': 1,
        '--edge-bottom': 1,
        '--edge-left': 1,
      }}
    >
      <div className="achievement-public-card-frame">
        <span className="public-card-edge public-card-edge-top" aria-hidden="true" />
        <span className="public-card-edge public-card-edge-right" aria-hidden="true" />
        <span className="public-card-edge public-card-edge-bottom" aria-hidden="true" />
        <span className="public-card-edge public-card-edge-left" aria-hidden="true" />
        <span className="public-card-glow" aria-hidden="true" />
        <AchievementRendererCard record={item} displayMode="board" />
      </div>
    </button>
  );
}

function AchievementTile({ item, onOpen, featured = false }) {
  return (
    <button
      type="button"
      data-achievement-id={item.id}
      onClick={() => onOpen(item)}
      className={`group w-full rounded-lg border border-olive-100 bg-white text-left shadow-sm transition hover:-translate-y-1 hover:border-olive-300 hover:shadow-xl ${featured ? 'p-4 lg:p-5' : 'p-3'}`}
    >
      <AchievementRendererCard record={item} displayMode={featured ? 'large' : 'tile'} />
      <div className="mt-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase text-olive-400">{achievementAsset(item)}</p>
          <p className={`${featured ? 'text-2xl' : 'text-lg'} font-black text-olive-900`}>{achievementTitle(item)}</p>
          <PublishedDate record={item} />
        </div>
        {item.isPublished && (
          <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold uppercase text-emerald-700">
            Published
          </span>
        )}
      </div>
    </button>
  );
}

function ManagementColumn({ title, emptyText, items, onOpen, onEditHref }) {
  return (
    <div className="rounded-lg border border-olive-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className={`text-sm font-black uppercase ${theme.textMuted}`}>{title}</h2>
        <span className="rounded-full bg-olive-50 px-2 py-1 text-xs font-bold text-olive-600">{items.length}</span>
      </div>

      {items.length === 0 ? (
        <p className={`py-8 text-center text-sm ${theme.textSecondary}`}>{emptyText}</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="space-y-2">
              <AchievementTile item={item} onOpen={onOpen} />
              {onEditHref && (
                <a
                  href={onEditHref(item)}
                  data-edit-achievement-id={item.id}
                  className="inline-flex rounded-lg border border-olive-200 bg-white px-3 py-2 text-xs font-bold uppercase text-olive-700 shadow-sm hover:bg-olive-50"
                >
                  Edit in studio
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PublicGallery({ items, onOpen }) {
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-300 bg-white/80 p-12 text-center shadow-sm">
        <p className="text-sm font-bold uppercase text-zinc-500">No published cards</p>
        <h2 className="mt-3 text-2xl font-bold text-zinc-950">Nothing here yet.</h2>
      </div>
    );
  }

  const rows = buildPublicRows(items);

  return (
    <div className="achievement-public-board">
      {rows.map((row) => (
        <div
          key={`${row.shape}-${row.items.map((item) => item.id).join('-')}`}
          className={`achievement-public-row is-${row.shape} count-${row.items.length}`}
        >
          {row.items.map((item) => (
            <PublicAchievementCard key={item.id} item={item} onOpen={onOpen} />
          ))}
        </div>
      ))}
    </div>
  );
}

function AchievementsPage() {
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [archiveError, setArchiveError] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [previewPhase, setPreviewPhase] = useState('closed');
  const [isReviewed, setIsReviewed] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteArmed, setIsDeleteArmed] = useState(false);
  const closePreviewTimerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadArchive() {
      setIsLoading(true);
      setArchiveError('');

      try {
        const response = await fetch(ARCHIVE_API_PATH);
        if (!response.ok) {
          throw new Error('Cards unavailable');
        }

        const payload = await response.json();
        if (isMounted) {
          setItems(Array.isArray(payload.items) ? payload.items : []);
        }
      } catch (error) {
        if (isMounted) {
          setItems([]);
          setArchiveError('Cards unavailable.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadArchive();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => () => {
    if (closePreviewTimerRef.current) {
      clearTimeout(closePreviewTimerRef.current);
    }
  }, []);

  const draftItems = useMemo(() => sortByRecent(items.filter((item) => !item.isPublished)), [items]);
  const publishedItems = useMemo(() => sortByRecent(items.filter((item) => item.isPublished)), [items]);
  const publishedBoardItems = useMemo(() => items.filter((item) => item.isPublished), [items]);
  const visibleItems = user ? items : publishedBoardItems;
  const isEmpty = !isLoading && !archiveError && visibleItems.length === 0;

  const openPreview = (record) => {
    if (closePreviewTimerRef.current) {
      clearTimeout(closePreviewTimerRef.current);
      closePreviewTimerRef.current = null;
    }

    setSelectedRecord(record);
    setPreviewPhase('open');
    setIsReviewed(false);
    setIsDeleteArmed(false);
  };

  const closePreview = () => {
    if (!selectedRecord) {
      setPreviewPhase('closed');
      return;
    }

    if (closePreviewTimerRef.current) {
      clearTimeout(closePreviewTimerRef.current);
    }

    setPreviewPhase('closing');
    setIsReviewed(false);
    setIsPublishing(false);
    setIsUnpublishing(false);
    setIsDeleting(false);
    setIsDeleteArmed(false);

    closePreviewTimerRef.current = setTimeout(() => {
      setSelectedRecord(null);
      setPreviewPhase('closed');
      closePreviewTimerRef.current = null;
    }, 320);
  };

  const publishSelected = async () => {
    if (!selectedRecord || !isReviewed || selectedRecord.isPublished) {
      return;
    }

    setIsPublishing(true);

    try {
      const response = await fetch(`${ARCHIVE_API_PATH}/${selectedRecord.id}/publish`, { method: 'POST' });
      if (!response.ok) {
        throw new Error('Publish failed');
      }

      const publishedRecord = await response.json();
      setItems((currentItems) => currentItems.map((item) => (
        item.id === publishedRecord.id ? publishedRecord : item
      )));
      setSelectedRecord(publishedRecord);
      setIsReviewed(false);
    } catch (error) {
      setArchiveError('Publish failed.');
    } finally {
      setIsPublishing(false);
    }
  };

  const unpublishSelected = async () => {
    if (!selectedRecord || !selectedRecord.isPublished) {
      return;
    }

    setIsUnpublishing(true);

    try {
      const response = await fetch(`${ARCHIVE_API_PATH}/${selectedRecord.id}/unpublish`, { method: 'POST' });
      if (!response.ok) {
        throw new Error('Move to drafts failed');
      }

      const draftRecord = await response.json();
      setItems((currentItems) => currentItems.map((item) => (
        item.id === draftRecord.id ? draftRecord : item
      )));
      setSelectedRecord(draftRecord);
      setIsReviewed(false);
      setIsDeleteArmed(false);
    } catch (error) {
      setArchiveError('Move to drafts failed.');
    } finally {
      setIsUnpublishing(false);
    }
  };

  const deleteSelectedDraft = async () => {
    if (!selectedRecord || selectedRecord.isPublished) {
      return;
    }

    if (!isDeleteArmed) {
      setIsDeleteArmed(true);
      return;
    }

    setIsDeleting(true);

    try {
      const response = await fetch(`${ARCHIVE_API_PATH}/${selectedRecord.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error('Delete failed');
      }

      setItems((currentItems) => currentItems.filter((item) => item.id !== selectedRecord.id));
      closePreview();
    } catch (error) {
      setArchiveError('Delete failed.');
      setIsDeleting(false);
    }
  };

  return (
    <section className={user ? 'space-y-6' : 'achievement-public-shell'}>
      <div className={user ? 'rounded-lg border border-olive-200 bg-gradient-to-br from-white via-olive-50 to-sky-50 p-6 shadow-sm' : 'achievement-public-hero'}>
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            {user ? (
              <>
                <p className={`text-sm font-semibold uppercase ${theme.textMuted}`}>Achievements</p>
                <h1 className={`mt-2 text-4xl font-bold ${theme.textPrimary}`}>Forecast cards</h1>
                <p className={`mt-3 max-w-2xl ${theme.textSecondary}`}>Saved forecast cards.</p>
              </>
            ) : (
              <h1 className="achievement-public-title">Achievement Board</h1>
            )}
          </div>

          {user && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={BOARD_EDITOR_PATH}
                className="inline-flex items-center justify-center rounded-lg border border-olive-200 bg-white px-5 py-3 text-sm font-semibold text-olive-700 shadow-sm hover:bg-olive-50"
              >
                Edit the board
              </a>
              <a
                href={ACHIEVEMENT_STUDIO_PATH}
                className={`inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold shadow-sm ${theme.btnPrimary}`}
              >
                Enter studio
              </a>
            </div>
          )}
        </div>
      </div>

      {archiveError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
          {archiveError}
        </div>
      )}

      {isLoading ? (
        <div className="rounded-lg border border-olive-200 bg-white p-10 text-center shadow-sm">
          <p className={`text-sm font-bold uppercase ${theme.textMuted}`}>Loading</p>
        </div>
      ) : user ? (
        <div className="grid gap-4 xl:grid-cols-2">
          <ManagementColumn title="Drafts" emptyText="No saved cards." items={draftItems} onOpen={openPreview} onEditHref={(item) => `${ACHIEVEMENT_STUDIO_PATH}?edit=${item.id}`} />
          <ManagementColumn title="Published" emptyText="No published cards." items={publishedItems} onOpen={openPreview} onEditHref={(item) => `${ACHIEVEMENT_STUDIO_PATH}?edit=${item.id}`} />
        </div>
      ) : (
        <PublicGallery items={publishedBoardItems} onOpen={openPreview} />
      )}

      {isEmpty && user && (
        <div className="rounded-lg border border-dashed border-olive-200 bg-white p-10 text-center shadow-sm">
          <p className={`text-sm font-bold uppercase ${theme.textMuted}`}>No cards yet</p>
          <h2 className={`mt-3 text-2xl font-bold ${theme.textPrimary}`}>Nothing here yet.</h2>
          <p className={`mx-auto mt-3 max-w-xl ${theme.textSecondary}`}>Open Studio to create the first card.</p>
        </div>
      )}

      {selectedRecord && (
        <div
          className={`achievement-modal-overlay is-${previewPhase}`}
          role="dialog"
          aria-modal="true"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closePreview();
            }
          }}
        >
          <div className="achievement-modal-shell">
            <div className="achievement-modal-head">
              <div>
                <p className={`text-xs font-black uppercase ${theme.textMuted}`}>{achievementAsset(selectedRecord)}</p>
                <h2 className={`mt-1 text-2xl font-black ${theme.textPrimary}`}>{achievementTitle(selectedRecord)}</h2>
                <PublishedDate record={selectedRecord} />
              </div>
              <button
                type="button"
                onClick={closePreview}
                aria-label="Close preview"
                className="achievement-modal-close"
              >
                Close
              </button>
            </div>

            <div className="achievement-modal-stage">
              <AchievementRendererCard record={selectedRecord} displayMode="modal" />
            </div>

            {user && (
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {selectedRecord.isPublished ? (
                  <span className="inline-flex w-fit rounded-full bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700">
                    Published
                  </span>
                ) : (
                  <label className="flex items-center gap-3 text-sm font-semibold text-olive-700">
                    <input
                      type="checkbox"
                      checked={isReviewed}
                      onChange={(event) => setIsReviewed(event.target.checked)}
                      className="h-5 w-5 rounded border-olive-300 text-olive-600"
                    />
                    I reviewed this card
                  </label>
                )}

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <a
                    href={`${ACHIEVEMENT_STUDIO_PATH}?edit=${selectedRecord.id}`}
                    className="rounded-lg border border-olive-200 bg-white px-5 py-3 text-sm font-bold text-olive-700 shadow-sm hover:bg-olive-50"
                  >
                    Edit in studio
                  </a>
                  {selectedRecord.isPublished ? (
                    <button
                      type="button"
                      onClick={unpublishSelected}
                      disabled={isUnpublishing}
                      className="rounded-lg border border-olive-200 bg-white px-5 py-3 text-sm font-bold text-olive-700 shadow-sm hover:bg-olive-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isUnpublishing ? 'Moving' : 'Move to drafts'}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={deleteSelectedDraft}
                        disabled={isDeleting}
                        className={`rounded-lg border px-5 py-3 text-sm font-bold shadow-sm disabled:cursor-not-allowed disabled:opacity-60 ${isDeleteArmed ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-olive-200 bg-white text-olive-700 hover:bg-olive-50'}`}
                      >
                        {isDeleting ? 'Deleting' : isDeleteArmed ? 'Confirm delete' : 'Delete draft'}
                      </button>
                      <button
                        type="button"
                        onClick={publishSelected}
                        disabled={!isReviewed || isPublishing}
                        className={`rounded-lg px-5 py-3 text-sm font-bold shadow-sm disabled:cursor-not-allowed disabled:bg-olive-100 disabled:text-olive-400 ${theme.btnPrimary}`}
                      >
                        {isPublishing ? 'Publishing' : 'Publish'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default AchievementsPage;
